import React, { useState, useMemo, useRef } from "react";
import styled, { css } from "styled-components";
import ComputationalMotifViewer from "./ComputationalMotifViewer";

/* ─── Constants ──────────────────────────────────────── */
const NODE_COLOR = { gene: "#003594", prot: "#B87333", tf: "#770538" };
const NODE_LABEL = {
  gene: "gene",
  prot: "protein amount",
  tf: "protein activate",
};

const NODE_NOTES = {
  A_gene: {
    title: "X_gene",
    sections: [
      { label: "Represents", items: [{ text: "Gene activity", cls: "" }] },
    ],
  },
  A_prot: {
    title: "X_prot (RNA / protein amount)",
    sections: [
      {
        label: "Represents",
        items: [{ text: "RNA amount → protein amount", cls: "" }],
      },
      {
        label: "Processes",
        items: [
          { text: "Transcription (gene → RNA)", cls: "pos" },
          { text: "Translation (RNA → protein)", cls: "pos" },
        ],
      },
      { label: "Data collection", items: [{ text: "RNAseq", cls: "" }] },
    ],
  },
  A_prot_act: {
    title: "X_prot_act",
    sections: [
      { label: "Represents", items: [{ text: "Protein activity", cls: "" }] },
      { label: "Data collection", items: [{ text: "RPPA", cls: "" }] },
    ],
  },
  Gene_pos_reg: {
    title: "Positive gene regulator",
    sections: [
      {
        label: "Examples",
        items: [
          { text: "Transcription factors (TFs)", cls: "pos" },
        ],
      },
      {
        label: "Effect",
        items: [{ text: "Activates transcription of X_gene", cls: "pos" }],
      },
    ],
  },
  Gene_neg_reg: {
    title: "Negative gene regulator",
    sections: [
      {
        label: "Examples",
        items: [
          { text: "Gene inhibitors / repressors", cls: "neg" },
        ],
      },
      {
        label: "Effect",
        items: [{ text: "Represses transcription of X_gene", cls: "neg" }],
      },
    ],
  },
  Prot_pos_reg: {
    title: "Positive protein regulator",
    sections: [
      {
        label: "Examples",
        items: [
          {
            text: "Activating Post-translational modification (PTM)",
            cls: "pos",
          },
        ],
      },
      {
        label: "Effect",
        items: [{ text: "Increases X_prot_act activity", cls: "pos" }],
      },
    ],
  },
  Prot_neg_reg: {
    title: "Negative protein regulator",
    sections: [
      {
        label: "Examples",
        items: [
          {
            text: "Inhibitory post-translational modification (PTM)",
            cls: "neg",
          },
        ],
      },
      {
        label: "Effect",
        items: [{ text: "Decreases X_prot_act activity", cls: "neg" }],
      },
    ],
  },
  tl_cytoplasmic: {
    title: "Cytoplasmic translocation",
    sections: [
      {
        label: "Description",
        items: [
          {
            text: "Movement of an element from another compartment to the cytoplasm.",
            cls: "",
          },
          {
            text: "The cytoplasm is assumed to be the default subcellular location for all elements unless otherwise specified.",
            cls: "",
          },
        ],
      },
    ],
  },
  tl_mitochondrial: {
    title: "Mitochondrial translocation",
    sections: [
      {
        label: "Description",
        items: [
          {
            text: "Movement of an element from the cytoplasm to the mitochondria.",
            cls: "",
          },
        ],
      },
    ],
  },
  tl_nuclear: {
    title: "Nuclear translocation",
    sections: [
      {
        label: "Description",
        items: [
          {
            text: "Movement of an element from the cytoplasm into the nucleus.",
            cls: "",
          },
        ],
      },
    ],
  },
  tl_membrane: {
    title: "Membrane recruitment",
    sections: [
      {
        label: "Description",
        items: [
          {
            text: "Movement of an element from the cytoplasm to the membrane.",
            cls: "",
          },
        ],
      },
    ],
  },
  cx_complex_formation: {
    title: "Complex formation",
    sections: [
      {
        label: "Description",
        items: [{ text: "Binding of two or more elements.", cls: "" }],
      },
    ],
  },
  cx_complex_pos_formation: {
    title: "Positive regulation of complex formation",
    sections: [
      {
        label: "Description",
        items: [
          {
            text: "Binding of two or more elements induced by positive regulator.",
            cls: "",
          },
        ],
      },
    ],
  },
  cx_complex_neg_formation: {
    title: "Negative regulation of complex formation",
    sections: [
      {
        label: "Description",
        items: [
          {
            text: "Binding of two or more elements induced by negative regulator.",
            cls: "",
          },
        ],
      },
    ],
  },
  bio_process: {
    title: "Biological process regulation",
    sections: [
      {
        label: "Description",
        items: [
          {
            text: "Positive regulators of a biological process are biomarkers of that process.",
            cls: "pos",
          },
          {
            text: "Negative regulators of a biological process are biomarkers of the absence of that process, or other processes that are suppressing that process, or are mutually exclusive with it.",
            cls: "neg",
          },
        ],
      },
    ],
  },
};

const BASE_NODES = [
  { id: "A_gene", type: "gene", label: "A_gene", x: 120, y: 140 },
  { id: "A_prot", type: "prot", label: "A_prot", x: 340, y: 140 },
  { id: "A_prot_act", type: "tf", label: "A_prot_act", x: 560, y: 140 },
];
const BASE_EDGES = [
  { src: "A_gene", tgt: "A_prot", type: "activate" },
  { src: "A_prot", tgt: "A_prot_act", type: "activate" },
];

const GRP_ITEMS = [
  {
    key: "gene-regulation",
    label: "Gene regulation",
    sub: [
      { key: "baseline-gene", label: "Baseline gene expression", locked: true },
      { key: "pos-reg", label: "Positive gene regulation", locked: false },
      { key: "neg-reg", label: "Negative gene regulation", locked: false },
    ],
  },
  {
    key: "transcription-translation",
    label: "Transcription + Translation",
    sub: [
      { key: "transcription", label: "Transcription", locked: true },
      { key: "translation", label: "Translation", locked: true },
    ],
  },
  {
    key: "protein-regulation",
    label: "Protein regulation",
    sub: [
      {
        key: "baseline-prot",
        label: "Baseline protein activity",
        locked: true,
      },
      {
        key: "prot-pos-reg",
        label: "Positive protein regulation",
        locked: false,
      },
      {
        key: "prot-neg-reg",
        label: "Negative protein regulation",
        locked: false,
      },
    ],
  },
];

const OTHER_ITEMS = [
  {
    key: "translocation",
    label: "Translocation",
    sub: [
      {
        key: "cytoplasmic",
        label: "Cytoplasmic translocation",
        radio: "location",
      },
      {
        key: "mitochondrial",
        label: "Mitochondrial translocation",
        radio: "location",
      },
      { key: "nuclear", label: "Nuclear translocation", radio: "location" },
      { key: "membrane", label: "Membrane recruitment", radio: "location" },
      { key: "pos-reg", label: "Positive regulation" },
      { key: "neg-reg", label: "Negative regulation" },
    ],
  },
  {
    key: "complex",
    label: "Complex",
    sub: [
      { key: "complex-formation", label: "Complex formation" },
      {
        key: "complex-pos-formation",
        label: "Positive regulation of complex formation",
        indent: true,
      },
      {
        key: "complex-neg-formation",
        label: "Negative regulation of complex formation",
        indent: true,
      },
      { key: "baseline-complex-act", label: "Baseline complex activation" },
      {
        key: "complex-pos-activity",
        label: "Positive regulation of complex activity",
        indent: true,
      },
      {
        key: "complex-neg-activity",
        label: "Negative regulation of complex activity",
        indent: true,
      },
    ],
  },
  {
    key: "indirect-regulation",
    label: "Indirect regulation",
    sub: [
      { key: "pos-reg", label: "Positive regulation" },
      { key: "neg-reg", label: "Negative regulation" },
    ],
  },
  {
    key: "chemical-reaction",
    label: "Chemical reaction",
    sub: [
      { key: "pos-reg", label: "Positive regulation" },
      { key: "neg-reg", label: "Negative regulation" },
    ],
  },
  {
    key: "biomarker-readout",
    label: "Biological process regulation",
    sub: [
      { key: "pos-reg", label: "Positive regulation" },
      { key: "neg-reg", label: "Negative regulation" },
    ],
  },
];

const COMPUTATIONAL_ITEMS = [
  {
    key: "single-regulator",
    label: "Single regulator",
    groups: [
      {
        label: "Single regulator",
        radio: true,
        sub: [
          { key: "basic-positive", label: "Basic positive" },
          { key: "inverted-positive", label: "Inverted positive" },
          { key: "basic-negative", label: "Basic negative" },
          { key: "inverted-negative", label: "Inverted negative" },
        ],
      },
      {
        label: "Regulation strength",
        radio: true,
        sub: [
          { key: "strength-regular", label: "Regular" },
          { key: "strength-weak", label: "Weak" },
        ],
      },
      {
        label: "Regulation weight",
        radio: true,
        sub: [
          { key: "weight-none", label: "None" },
          { key: "weight-yes", label: "Yes" },
        ],
      },
      {
        label: "Number of levels",
        sub: [
          { key: "levels-2", label: "2" },
          { key: "levels-3", label: "3" },
          { key: "levels-4", label: "4" },
          { key: "levels-5", label: "5" },
        ],
        grid: true,
      },
      {
        label: "Memory",
        radio: true,
        sub: [
          { key: "memory-without", label: "Without memory" },
          { key: "memory-with", label: "With memory" },
        ],
      },
      {
        label: "Spontaneous behavior",
        radio: true,
        sub: [
          { key: "spont-none", label: "None" },
          { key: "spont-yes", label: "Yes" },
          { key: "spont-increase", label: "Yes, increase only" },
          { key: "spont-decrease", label: "Yes, decrease only" },
        ],
      },
      {
        label: "Increment",
        radio: true,
        sub: [
          { key: "increment-prop", label: "Proportional to regulation" },
          { key: "increment-zero", label: "0 (set to 1 level per step)" },
          { key: "increment-other", label: "Other" },
        ],
      },
    ],
  },
  {
    key: "multiple-regulators",
    label: "Multiple regulators",
    sub: [],
    comingSoon: true,
  },
  {
    key: "conditional-reg",
    label: "Conditional regulation",
    sub: [],
    comingSoon: true,
  },
  {
    key: "special-reg",
    label: "Special regulation",
    sub: [],
    comingSoon: true,
  },
];

/* ─── Single-select groups for computational motifs ──── */
// Keys in the same group are mutually exclusive (radio behaviour).
// "levels-*" is intentionally NOT in this list — levels stay multi-select.
const SINGLE_SELECT_GROUPS = {
  "single-regulator:basic-positive":    "sr-type",
  "single-regulator:inverted-positive": "sr-type",
  "single-regulator:basic-negative":    "sr-type",
  "single-regulator:inverted-negative": "sr-type",

  "single-regulator:strength-regular": "sr-strength",
  "single-regulator:strength-weak":    "sr-strength",

  "single-regulator:weight-none": "sr-weight",
  "single-regulator:weight-yes":  "sr-weight",

  "single-regulator:memory-without": "sr-memory",
  "single-regulator:memory-with":    "sr-memory",

  "single-regulator:spont-none":     "sr-spont",
  "single-regulator:spont-yes":      "sr-spont",
  "single-regulator:spont-increase": "sr-spont",
  "single-regulator:spont-decrease": "sr-spont",

  "single-regulator:increment-prop":  "sr-increment",
  "single-regulator:increment-zero":  "sr-increment",
  "single-regulator:increment-other": "sr-increment",
};

/* ─── Styled components ──────────────────────────────── */
const Root = styled.div`
  min-height: 100vh;
  width: 100%;
  background: #fff;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  display: flex;
  box-sizing: border-box;
`;

const LeftPanel = styled.div`
  width: 300px;
  flex-shrink: 0;
  padding: 36px 24px 36px 36px;
  border-right: 1px solid #eee;
  overflow-y: auto;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #111;
  margin: 0 0 20px 0;
`;

const ModeRow = styled.div`
  display: flex;
  margin-bottom: 24px;
  border-radius: 6px;
  overflow: hidden;
  border: 1.5px solid #99b3d4;
`;

const ModeBtn = styled.button`
  flex: 1;
  padding: 9px 4px;
  background: #fff;
  border: none;
  font-size: 11px;
  font-weight: 500;
  color: #888;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s, color 0.15s;
  line-height: 1.3;
  text-align: center;

  &:not(:last-child) { border-right: 1.5px solid #99b3d4; }
  &:hover { background: #f0f5ff; color: #111; }
  ${({ $active }) =>
    $active &&
    css`
      background: #DBEEFF;
      color: #111;
      font-weight: 600;
    `}
`;

const Group = styled.div`
  margin-bottom: 24px;
`;

const GroupLabel = styled.div`
  font-size: 12px;
  color: #555;
  margin-bottom: 7px;
`;

const SectionHeader = styled.div`
  display: block;
  width: 100%;
  padding: 10px 14px;
  background: #DBEEFF;
  border-radius: 4px 4px 0 0;
  font-size: 14px;
  font-weight: 400;
  color: #111;
  text-align: left;
  margin-bottom: 0;
  box-sizing: border-box;
  user-select: none;
`;

const CatBtn = styled.button`
  display: block;
  width: 100%;
  padding: 10px 14px;
  background: ${({ $open }) => ($open ? "#99b3d4" : "#DBEEFF")};
  border: none;
  border-radius: ${({ $open }) => ($open ? "4px 4px 0 0" : "4px")};
  font-size: 14px;
  font-weight: 400;
  color: #111;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.13s;
  margin-bottom: ${({ $open }) => ($open ? "0" : "5px")};
  box-sizing: border-box;

  &:hover { background: #99b3d4; }
`;

const SubList = styled.div`
  margin-bottom: 6px;
`;

const IndentedSubRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 14px 7px 28px;
  border: 1px solid #c2d4ed;
  border-top: none;
  background: ${({ $checked }) => ($checked ? "#f0f5ff" : "#fff")};
  cursor: pointer;
  transition: background 0.12s;
  box-sizing: border-box;
  border-left: 3px solid #99b3d4;

  &:last-child { border-radius: 0 0 4px 4px; }
  &:hover { background: #f0f5ff; }
`;

const StyledSubRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border: 1px solid #c2d4ed;
  border-top: none;
  background: ${({ $checked, $locked }) =>
    $checked || $locked ? "#f0f5ff" : "#fff"};
  cursor: ${({ $locked }) => ($locked ? "default" : "pointer")};
  transition: background 0.12s;
  box-sizing: border-box;

  &:last-child { border-radius: 0 0 4px 4px; }
  &:hover { background: #f0f5ff; }
`;

const CheckboxBox = styled.div`
  width: 15px;
  height: 15px;
  border: 1.5px solid ${({ $checked }) => ($checked ? "#003594" : "#bbb")};
  border-radius: 2px;
  background: ${({ $checked }) => ($checked ? "#003594" : "#fff")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.12s;
  opacity: ${({ $locked }) => ($locked ? 0.75 : 1)};
`;

const SubLabelText = styled.span`
  font-size: 12px;
  color: ${({ $locked }) => ($locked ? "#555" : "#222")};
  line-height: 1.4;
`;

const RightPanel = styled.div`
  flex: 1;
  width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  background: #f8f9fb;
  padding: 32px;
  box-sizing: border-box;
  overflow-y: auto;
`;

const CanvasWrap = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 720px;
  position: relative;
`;

const Legend = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-self: flex-start;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #666;
  font-family: monospace;
`;

const LegendDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $color }) => $color};
`;

const Placeholder = styled.p`
  font-size: 12px;
  color: #aaa;
  margin-top: 4px;
`;

const ClickHint = styled.p`
  font-size: 11px;
  color: #003594;
  margin-top: 8px;
  font-style: italic;
`;

const RadioRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border: 1px solid #c2d4ed;
  border-top: none;
  background: ${({ $selected }) => ($selected ? "#f0f5ff" : "#fff")};
  cursor: pointer;
  transition: background 0.12s;
  box-sizing: border-box;

  &:last-child { border-radius: 0 0 4px 4px; }
  &:hover { background: #f0f5ff; }
`;

const RadioCircle = styled.div`
  width: 15px;
  height: 15px;
  border: 1.5px solid ${({ $selected }) => ($selected ? "#003594" : "#bbb")};
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.12s;
`;

const RadioDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #003594;
`;

const RadioGroupHint = styled.div`
  font-size: 10px;
  color: #888;
  font-style: italic;
  padding: 5px 14px 2px;
  border: 1px solid #c2d4ed;
  border-top: none;
  background: #fff;
  box-sizing: border-box;
`;

const ComingSoon = styled.div`
  padding: 10px 14px;
  font-size: 11px;
  color: #bbb;
  font-style: italic;
  border: 1px solid #c2d4ed;
  border-top: none;
  border-radius: 0 0 4px 4px;
  background: #fff;
  box-sizing: border-box;
`;

const CompGroupLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #111;
  background: #DBEEFF;
  padding: 7px 14px;
  border: 1px solid #c2d4ed;
  border-top: none;
  box-sizing: border-box;
`;

const CompGridList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-left: 1px solid #c2d4ed;
  border-right: 1px solid #c2d4ed;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 380px;
  gap: 14px;
`;

const EmptyIcon = styled.div`
  opacity: 0.5;
`;

const EmptyText = styled.p`
  font-size: 13px;
  color: #bbb;
  font-style: italic;
  text-align: center;
  max-width: 220px;
  line-height: 1.6;
  margin: 0;
`;

/* ── Note popup styled components ── */
const NotePopupWrap = styled.div`
  position: absolute;
  z-index: 10;
  background: #fff;
  border: 1.5px solid #99b3d4;
  border-radius: 8px;
  padding: 12px 14px;
  min-width: 190px;
  max-width: 250px;
  box-shadow: 0 4px 20px rgba(176, 96, 160, 0.15);
  pointer-events: all;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top}px;
`;

const NoteTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const NoteTitle = styled.h4`
  font-size: 12px;
  font-weight: 600;
  color: #003594;
  margin: 0;
`;

const NoteCloseBtn = styled.button`
  width: 18px;
  height: 18px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bbb;
  border-radius: 3px;
  transition: background 0.12s, color 0.12s;
  flex-shrink: 0;

  &:hover { background: #DBEEFF; color: #003594; }
`;

const NoteSectionWrap = styled.div`
  margin-bottom: 7px;
  &:last-child { margin-bottom: 0; }
`;

const NoteLabelText = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 3px;
`;

const NoteItemRow = styled.div`
  font-size: 11px;
  color: #333;
  line-height: 1.5;
  padding-left: 8px;
  border-left: 2px solid
    ${({ $cls }) =>
      $cls === "pos" ? "#66B2E3" : $cls === "neg" ? "#FF5B45" : "#DBEEFF"};
  margin-bottom: 2px;
`;

const CanvasSvgWrap = styled.div`
  position: relative;
`;

/* ─── Computational compare bar ─────────────────────── */
const CompareBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  width: 100%;
`;

const AddBtn = styled.button`
  padding: 7px 18px;
  background: #003594;
  color: #fff;
  border: none;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.13s;
  white-space: nowrap;

  &:hover { background: #00205B; }
  &:disabled {
    background: #99b3d4;
    cursor: default;
  }
`;

const ClearBtn = styled.button`
  padding: 7px 14px;
  background: #fff;
  color: #003594;
  border: 1.5px solid #99b3d4;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.13s, color 0.13s;
  white-space: nowrap;

  &:hover { background: #f0f5ff; }
`;

const ConfigPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #f0f5ff;
  border: 1.5px solid #99b3d4;
  border-radius: 20px;
  font-size: 11px;
  color: #003594;
  font-weight: 500;
`;

const PillRemove = styled.button`
  width: 14px;
  height: 14px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6699cc;
  border-radius: 50%;
  transition: background 0.1s;

  &:hover { background: #DBEEFF; color: #003594; }
`;

const CompareCount = styled.span`
  font-size: 11px;
  color: #aaa;
`;

/* ─── NotePopup component ────────────────────────────── */
const NotePopup = ({ nodeId, svgNode, svgRef, onClose }) => {
  const note = NODE_NOTES[nodeId];
  if (!note || !svgRef.current) return null;

  const svgRect = svgRef.current.getBoundingClientRect();
  const svgW = svgRef.current.viewBox.baseVal.width;
  const svgH = svgRef.current.viewBox.baseVal.height;
  const scaleX = svgRect.width / svgW;
  const scaleY = svgRect.height / svgH;

  const px = svgNode.x * scaleX;
  const py = svgNode.y * scaleY;

  const popupW = 230;
  let left = px - popupW / 2;
  let top = py + 34 * scaleY + 10;

  if (left < 4) left = 4;
  if (left + popupW > svgRect.width - 4) left = svgRect.width - popupW - 4;
  if (top + 180 > svgRect.height) top = py - 34 * scaleY - 185;
  if (top < 8) top = 8;

  return (
    <NotePopupWrap $left={left} $top={top}>
      <NoteTitleRow>
        <NoteTitle>{note.title}</NoteTitle>
        <NoteCloseBtn
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          aria-label="Close"
        >
          <svg width="10" height="10" viewBox="0 0 10 10">
            <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </NoteCloseBtn>
      </NoteTitleRow>
      {note.sections.map((sec, i) => (
        <NoteSectionWrap key={i}>
          <NoteLabelText>{sec.label}</NoteLabelText>
          {sec.items.map((item, j) => (
            <NoteItemRow key={j} $cls={item.cls}>{item.text}</NoteItemRow>
          ))}
        </NoteSectionWrap>
      ))}
    </NotePopupWrap>
  );
};

/* ─── MotifCanvas ────────────────────────────────────── */
const MotifCanvas = ({ nodes, edges, isBiological, activeNode, onNodeClick }) => {
  const maxY = nodes.reduce((m, n) => Math.max(m, n.y), 0);
  const W = 720, H = Math.max(380, maxY + 80);
  const svgRef = useRef(null);
  const getPos = (id) => nodes.find((n) => n.id === id) || { x: 0, y: 0 };

  const renderEdge = (e, i) => {
    const src = getPos(e.src);
    const tgt = getPos(e.tgt);
    const color = e.type === "activate" ? "#66B2E3" : "#FF5B45";
    const markId = "mk-" + i;
    const dx = tgt.x - src.x, dy = tgt.y - src.y;
    const dist = Math.hypot(dx, dy) || 1;
    const srcR = src.type === "junction" ? 9 : src.type === "dashed" ? 52 : src.type === "prot" || src.type === "tf" || src.type === "gene" ? 44 : 28;
    const tgtR = tgt.type === "junction" ? 9 : tgt.type === "dashed" ? 52 : tgt.type === "prot" || tgt.type === "tf" || tgt.type === "gene" ? 44 : 28;
    const x1 = src.x + (dx / dist) * srcR;
    const y1 = src.y + (dy / dist) * srcR;
    const x2 = tgt.x - (dx / dist) * (tgtR + 8);
    const y2 = tgt.y - (dy / dist) * (tgtR + 8);
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const hasNote = !!e.noteKey;
    const labelW = e.label ? e.label.length * 5.6 + 16 : 0;

    return (
      <g key={i}>
        <defs>
          <marker id={markId} markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            {e.type === "activate"
              ? <polygon points="0,0 8,4 0,8" fill={color} />
              : <line x1="1" y1="1" x2="1" y2="7" stroke={color} strokeWidth="2.5" />}
          </marker>
        </defs>
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="1.6"
          strokeDasharray={e.dashed || e.type === "inhibit" ? "5,3" : "none"}
          markerEnd={"url(#" + markId + ")"} />
        {e.label && (
          <g
            style={{ cursor: hasNote ? "pointer" : "default" }}
            onClick={hasNote ? (ev) => { ev.stopPropagation(); setActiveEdgeNote({ key: e.noteKey, mx, my }); } : undefined}
          >
            <rect
              x={mx - labelW / 2}
              y={my - 10}
              width={labelW}
              height={16}
              fill="#fff"
              stroke={hasNote ? "#003594" : color}
              strokeWidth={hasNote ? 1 : 0.5}
              rx={4}
              opacity={0.95}
            />
            <text
              x={mx}
              y={my + 3}
              textAnchor="middle"
              fontSize="9"
              fontFamily="-apple-system,sans-serif"
              fill={hasNote ? "#003594" : color}
              fontWeight="600"
            >
              {e.label}
            </text>
            {hasNote && (
              <>
                <circle cx={mx + labelW / 2 - 5} cy={my - 10} r={6} fill="#003594" />
                <text x={mx + labelW / 2 - 5} y={my - 6} textAnchor="middle" fontSize="8"
                  fontFamily="-apple-system,sans-serif" fill="#fff" fontWeight="700">i</text>
              </>
            )}
          </g>
        )}
      </g>
    );
  };

  const renderNode = (n) => {
    if (n.type === "junction") {
      return (
        <g key={n.id}>
          <circle cx={n.x} cy={n.y} r={9} fill="#fff" stroke="#B87333" strokeWidth="2" />
        </g>
      );
    }
    if (n.type === "dashed") {
      const lines = n.label.split("\n");
      return (
        <g key={n.id}>
          <ellipse cx={n.x} cy={n.y} rx={50} ry={30} fill="#fff" stroke="#B87333" strokeWidth="1.8" strokeDasharray="5,3" />
          {lines.map((ln, i) => (
            <text key={i} x={n.x} y={n.y + (lines.length === 1 ? 4 : i * 13 - 4)}
              textAnchor="middle" fontSize="10" fontFamily="-apple-system,sans-serif"
              fill="#B87333" fontWeight="600">{ln}</text>
          ))}
        </g>
      );
    }
    const color = NODE_COLOR[n.type];
    const lines = n.label.split("\n");
    const isActive = activeNode === n.id;
    const noteId = n.noteKey || n.id;
    const hasNote = isBiological ? !!NODE_NOTES[n.id] : !!NODE_NOTES[noteId];
    return (
      <g key={n.id} style={{ cursor: hasNote ? "pointer" : "default" }}
        onClick={hasNote ? (e) => { e.stopPropagation(); onNodeClick(noteId); } : undefined}>
        <ellipse cx={n.x} cy={n.y} rx={42} ry={28} fill={isActive ? color : "#fff"}
          stroke={color} strokeWidth={isActive ? 3 : 2.2} style={{ transition: "all 0.15s" }} />
        {lines.map((ln, i) => (
          <text key={i} x={n.x} y={n.y + (lines.length === 1 ? 4 : i * 13 - 4)}
            textAnchor="middle" fontSize="10" fontFamily="-apple-system,sans-serif"
            fill={isActive ? "#fff" : color} fontWeight="600">{ln}</text>
        ))}
        {hasNote && !isActive && (
          <>
            <circle cx={n.x + 36} cy={n.y - 22} r={7} fill="#003594" />
            <text x={n.x + 36} y={n.y - 18} textAnchor="middle" fontSize="9"
              fontFamily="-apple-system,sans-serif" fill="#fff" fontWeight="700">i</text>
          </>
        )}
      </g>
    );
  };

  const [activeEdgeNote, setActiveEdgeNote] = React.useState(null); // {key, mx, my}

  const activeNodeObj = activeNode
    ? nodes.find((n) => n.id === activeNode) || nodes.find((n) => n.noteKey === activeNode)
    : null;

  return (
    <CanvasSvgWrap>
      <svg ref={svgRef} viewBox={"0 0 " + W + " " + H} width="100%"
        style={{ display: "block" }}
        onClick={() => { onNodeClick(null); setActiveEdgeNote(null); }}>
        <rect width={W} height={H} fill="#fff" />
        {edges.map(renderEdge)}
        {nodes.map(renderNode)}
      </svg>
      {activeNode && activeNodeObj && (
        <NotePopup nodeId={activeNode} svgNode={activeNodeObj} svgRef={svgRef} onClose={() => onNodeClick(null)} />
      )}
      {activeEdgeNote && (
        <NotePopup
          nodeId={activeEdgeNote.key}
          svgNode={{ x: activeEdgeNote.mx, y: activeEdgeNote.my }}
          svgRef={svgRef}
          onClose={() => setActiveEdgeNote(null)}
        />
      )}
    </CanvasSvgWrap>
  );
};

/* ─── CheckmarkSvg ───────────────────────────────────── */
const CheckmarkSvg = () => (
  <svg width="9" height="9" viewBox="0 0 10 10">
    <polyline points="1.5,5 4,7.5 8.5,2.5" stroke="#fff" strokeWidth="2"
      fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ─── SubRowItem ─────────────────────────────────────── */
const SubRowItem = ({ s, sectionKey, checked, onToggle }) => {
  const isLocked = !!s.locked;
  const isChecked = isLocked || checked;
  const RowEl = s.indent ? IndentedSubRow : StyledSubRow;
  return (
    <RowEl $locked={isLocked} $checked={isChecked}
      onClick={isLocked ? undefined : () => onToggle(sectionKey + ":" + s.key)}>
      <CheckboxBox $checked={isChecked} $locked={isLocked}>
        {isChecked && <CheckmarkSvg />}
      </CheckboxBox>
      <SubLabelText $locked={isLocked}>{s.label}</SubLabelText>
    </RowEl>
  );
};

/* ─── CollapseSection ────────────────────────────────── */
const CollapseSection = ({ item, checkedKeys, onToggle, radioValues = {}, onRadio = () => {}, openKeys, onToggleOpen }) => {
  const isOpen = !!openKeys[item.key];

  if (item.groups) {
    return (
      <div>
        <CatBtn $open={isOpen} onClick={() => onToggleOpen(item.key)}>{item.label}</CatBtn>
        {isOpen && (
          <SubList>
            {item.groups.map((grp, gi) => (
              <div key={gi}>
                <CompGroupLabel>{grp.label}</CompGroupLabel>
                {grp.radio ? (
                  grp.sub.map((s) => {
                    const fullKey = item.key + ":" + s.key;
                    const selected = checkedKeys.has(fullKey);
                    return (
                      <RadioRow key={s.key} $selected={selected} onClick={() => onToggle(fullKey)}>
                        <RadioCircle $selected={selected}>{selected && <RadioDot />}</RadioCircle>
                        <SubLabelText>{s.label}</SubLabelText>
                      </RadioRow>
                    );
                  })
                ) : grp.grid ? (
                  <CompGridList>
                    {grp.sub.map((s) => (
                      <SubRowItem key={s.key} s={s} sectionKey={item.key}
                        checked={checkedKeys.has(item.key + ":" + s.key)} onToggle={onToggle} />
                    ))}
                  </CompGridList>
                ) : (
                  grp.sub.map((s) => (
                    <SubRowItem key={s.key} s={s} sectionKey={item.key}
                      checked={checkedKeys.has(item.key + ":" + s.key)} onToggle={onToggle} />
                  ))
                )}
              </div>
            ))}
          </SubList>
        )}
      </div>
    );
  }

  const radioGroups = [...new Set((item.sub || []).filter((s) => s.radio).map((s) => s.radio))];

  return (
    <div>
      <CatBtn $open={isOpen} onClick={() => onToggleOpen(item.key)}>{item.label}</CatBtn>
      {isOpen && item.comingSoon && <ComingSoon>Coming soon</ComingSoon>}
      {isOpen && !item.comingSoon && (
        <SubList>
          {radioGroups.length > 0 && <RadioGroupHint>Only one allowed or None</RadioGroupHint>}
          {(item.sub || []).map((s) => {
            if (s.radio) {
              const selected = radioValues[item.key + ":" + s.radio] === s.key;
              return (
                <RadioRow key={s.key} $selected={selected}
                  onClick={() => onRadio(item.key + ":" + s.radio, selected ? null : s.key)}>
                  <RadioCircle $selected={selected}>{selected && <RadioDot />}</RadioCircle>
                  <SubLabelText>{s.label}</SubLabelText>
                </RadioRow>
              );
            }
            return (
              <SubRowItem key={s.key} s={s} sectionKey={item.key}
                checked={checkedKeys.has(item.key + ":" + s.key)} onToggle={onToggle} />
            );
          })}
        </SubList>
      )}
    </div>
  );
};

/* ─── Helper: summarise a config Set into a short label ─ */
const summariseConfig = (keys) => {
  const has = (k) => keys.has(k);
  const type = has("single-regulator:basic-positive")    ? "Basic+"
             : has("single-regulator:inverted-positive") ? "Inv+"
             : has("single-regulator:basic-negative")    ? "Basic−"
             : has("single-regulator:inverted-negative") ? "Inv−"
             : "?";
  const levels = ["2","3","4","5"].filter(l => has(`single-regulator:levels-${l}`));
  const lvlStr = levels.length ? ` L${levels.join(",")}` : "";
  const mem = has("single-regulator:memory-with") ? " mem" : "";
  return `${type}${lvlStr}${mem}`;
};

/* ─── Main export ────────────────────────────────────── */
const COMPUTATIONAL_DEFAULTS = new Set([
  "single-regulator:basic-positive",
  "single-regulator:strength-regular",
  "single-regulator:weight-none",
  "single-regulator:memory-without",
  "single-regulator:spont-none",
  "single-regulator:increment-prop",
  "single-regulator:levels-2",
  "single-regulator:levels-3",
  "single-regulator:levels-4",
  "single-regulator:levels-5",
]);

const MotifSelector = ({ onSelect }) => {
  const [mode, setMode] = useState(null);
  const [checkedKeys, setCheckedKeys] = useState(new Set(COMPUTATIONAL_DEFAULTS));
  const [openKeys, setOpenKeys] = useState({ "single-regulator": true });
  const [activeNode, setActiveNode] = useState(null);
  const [radioValues, setRadioValues] = useState({});

  // ── Comparison configurations ──────────────────────────
  // Each entry: { id: number, keys: Set }
  const [configurations, setConfigurations] = useState([]);
  const nextIdRef = useRef(1);

  const switchMode = (newMode) => {
    setMode(newMode);
    setCheckedKeys(newMode === "computational" ? new Set(COMPUTATIONAL_DEFAULTS) : new Set());
    setOpenKeys(newMode === "computational" ? { "single-regulator": true } : {});
    setActiveNode(null);
    setRadioValues({});
  };

  const toggleCheck = (fullKey) =>
    setCheckedKeys((prev) => {
      const next = new Set(prev);

      if (next.has(fullKey)) {
        next.delete(fullKey);
        if (fullKey === "complex:complex-formation") {
          next.delete("complex:complex-pos-formation");
          next.delete("complex:complex-neg-formation");
        }
        if (fullKey === "complex:baseline-complex-act") {
          next.delete("complex:complex-pos-activity");
          next.delete("complex:complex-neg-activity");
        }
      } else {
        // ── Single-select radio behaviour ──────────────────
        const group = SINGLE_SELECT_GROUPS[fullKey];
        if (group) {
          Object.entries(SINGLE_SELECT_GROUPS)
            .filter(([, g]) => g === group)
            .forEach(([k]) => next.delete(k));
        }
        // ── end radio block ─────────────────────────────────

        const otherCategories = [
          "translocation", "complex", "indirect-regulation",
          "chemical-reaction", "biomarker-readout",
        ];
        if (otherCategories.includes(fullKey.split(":")[0])) {
          const keysToDelete = [...next].filter(
            (k) =>
              otherCategories.includes(k.split(":")[0]) &&
              k.split(":")[0] !== fullKey.split(":")[0],
          );
          keysToDelete.forEach((k) => next.delete(k));
        }
        next.add(fullKey);
        if (fullKey === "complex:complex-pos-formation" || fullKey === "complex:complex-neg-formation") {
          next.add("complex:complex-formation");
        }
        if (fullKey === "complex:complex-pos-activity" || fullKey === "complex:complex-neg-activity") {
          next.add("complex:baseline-complex-act");
        }
      }
      return next;
    });

  const toggleOpen = (key) =>
    setOpenKeys((prev) => ({ ...prev, [key]: !prev[key] }));

  const setRadio = (groupKey, value) => {
    const newCat = groupKey.split(":")[0];
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      [...next].filter((k) => k.split(":")[0] !== newCat).forEach((k) => next.delete(k));
      return next;
    });
    setRadioValues((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => { if (k.split(":")[0] === newCat) next[k] = v; });
      next[groupKey] = value;
      return next;
    });
  };

  // ── Add current config to comparison list ──────────────
  const [justAdded, setJustAdded] = useState(false);
  const [newestId, setNewestId] = useState(null);
  const justAddedTimer = useRef(null);

  const addConfiguration = () => {
    const snapshot = new Set(checkedKeys);
    const newId = nextIdRef.current++;
    setConfigurations((prev) => [...prev, { id: newId, keys: snapshot }]);
    setCheckedKeys(new Set(COMPUTATIONAL_DEFAULTS));
    setNewestId(newId);
    setJustAdded(true);
    clearTimeout(justAddedTimer.current);
    justAddedTimer.current = setTimeout(() => {
      setJustAdded(false);
      setNewestId(null);
    }, 1800);
  };

  const removeConfiguration = (id) =>
    setConfigurations((prev) => prev.filter((c) => c.id !== id));

  const clearConfigurations = () => setConfigurations([]);

  // Disable "Add" if the current checkedKeys is identical to an existing config
  const isDuplicate = configurations.some(
    (c) =>
      c.keys.size === checkedKeys.size &&
      [...checkedKeys].every((k) => c.keys.has(k)),
  );

  const { canvasNodes, canvasEdges } = useMemo(() => {
    const nodeMap = {};
    const edgeList = [];

    BASE_NODES.forEach((n) => { nodeMap[n.id] = { ...n }; });
    BASE_EDGES.forEach((e) => edgeList.push({ ...e }));

    const genePosOn = checkedKeys.has("gene-regulation:pos-reg");
    const geneNegOn = checkedKeys.has("gene-regulation:neg-reg");
    const protPosOn = checkedKeys.has("protein-regulation:prot-pos-reg");
    const protNegOn = checkedKeys.has("protein-regulation:prot-neg-reg");

    if (genePosOn) {
      const x = geneNegOn ? 60 : 120;
      nodeMap["Gene_pos_reg"] = { id: "Gene_pos_reg", type: "tf", label: "Positive\nregulation", x, y: 290 };
      edgeList.push({ src: "Gene_pos_reg", tgt: "A_gene", type: "activate" });
    }
    if (geneNegOn) {
      const x = genePosOn ? 180 : 120;
      nodeMap["Gene_neg_reg"] = { id: "Gene_neg_reg", type: "tf", label: "Negative\nregulation", x, y: 290 };
      edgeList.push({ src: "Gene_neg_reg", tgt: "A_gene", type: "inhibit" });
    }
    if (protPosOn) {
      const x = protNegOn ? 500 : 560;
      nodeMap["Prot_pos_reg"] = { id: "Prot_pos_reg", type: "tf", label: "Positive\nregulation", x, y: 290 };
      edgeList.push({ src: "Prot_pos_reg", tgt: "A_prot_act", type: "activate" });
    }
    if (protNegOn) {
      const x = protPosOn ? 620 : 560;
      nodeMap["Prot_neg_reg"] = { id: "Prot_neg_reg", type: "tf", label: "Negative\nregulation", x, y: 290 };
      edgeList.push({ src: "Prot_neg_reg", tgt: "A_prot_act", type: "inhibit" });
    }

    // Translocation motif
    if (mode === "other") {
      const locKey = radioValues["translocation:location"];
      const locationLabel = locKey === "cytoplasmic" ? "Cytoplasmic"
        : locKey === "mitochondrial" ? "Mitochondrial"
        : locKey === "nuclear" ? "Nuclear"
        : locKey === "membrane" ? "Membrane" : null;

      if (locationLabel !== null || checkedKeys.has("translocation:pos-reg") || checkedKeys.has("translocation:neg-reg")) {
        Object.keys(nodeMap).forEach((k) => delete nodeMap[k]);
        edgeList.length = 0;
        const srcLocation = locKey === "cytoplasmic" ? "Location:\n<original loc>"
          : locKey === "mitochondrial" ? "Location:\ncytoplasm"
          : locKey === "nuclear" ? "Location:\ncytoplasm"
          : locKey === "membrane" ? "Location:\ncytoplasm" : "Location:\nsource";
        const tgtLocation = locKey === "cytoplasmic" ? "Location:\ncytoplasm"
          : locKey === "mitochondrial" ? "Location:\nmitochondria"
          : locKey === "nuclear" ? "Location:\nnucleus"
          : locKey === "membrane" ? "Location:\nmembrane" : "Location:\ntarget";
        const posOn = checkedKeys.has("translocation:pos-reg");
        const negOn = checkedKeys.has("translocation:neg-reg");
        const tlNoteKey = locKey ? "tl_" + locKey : null;
        nodeMap["tl_src"] = { id: "tl_src", type: "prot", label: "element\n" + srcLocation, x: 220, y: 180 };
        nodeMap["tl_tgt"] = { id: "tl_tgt", type: "prot", label: "element\n" + tgtLocation, x: 480, y: 180 };
        const arrowLabel = locationLabel ? locationLabel : "translocation";
        edgeList.push({ src: "tl_src", tgt: "tl_tgt", type: "activate", label: arrowLabel, noteKey: tlNoteKey });
        if (posOn) {
          const x = negOn ? 155 : 185;
          nodeMap["tl_pos"] = { id: "tl_pos", type: "tf", label: "positive\nregulation", x, y: 300 };
          edgeList.push({ src: "tl_pos", tgt: "tl_src", type: "activate" });
        }
        if (negOn) {
          const x = posOn ? 285 : 255;
          nodeMap["tl_neg"] = { id: "tl_neg", type: "tf", label: "negative\nregulation", x, y: 300 };
          edgeList.push({ src: "tl_neg", tgt: "tl_src", type: "inhibit" });
        }
      }
    }

    // Complex motif
    if (mode === "other") {
      const complexFormation = checkedKeys.has("complex:complex-formation");
      const complexPosForm = checkedKeys.has("complex:complex-pos-formation");
      const complexNegForm = checkedKeys.has("complex:complex-neg-formation");
      const baselineComplexAct = checkedKeys.has("complex:baseline-complex-act");
      const complexPosAct = checkedKeys.has("complex:complex-pos-activity");
      const complexNegAct = checkedKeys.has("complex:complex-neg-activity");

      if (complexFormation || complexPosForm || complexNegForm || baselineComplexAct || complexPosAct || complexNegAct) {
        Object.keys(nodeMap).forEach((k) => delete nodeMap[k]);
        edgeList.length = 0;
        const cxNoteKey = complexPosForm ? "cx_complex_pos_formation"
          : complexNegForm ? "cx_complex_neg_formation"
          : complexFormation ? "cx_complex_formation" : null;

        if (complexFormation) {
          nodeMap["cx_prot1"] = { id: "cx_prot1", type: "prot", label: "protein 1\namount", x: 200, y: 80 };
          nodeMap["cx_prot2"] = { id: "cx_prot2", type: "prot", label: "protein 2\namount", x: 400, y: 80 };
          nodeMap["cx_junction"] = { id: "cx_junction", type: "junction", label: "", x: 300, y: 185 };
          const complexY = baselineComplexAct ? 270 : 300;
          nodeMap["cx_complex"] = { id: "cx_complex", type: "prot", label: "complex\namount", x: 300, y: complexY, noteKey: cxNoteKey };
          edgeList.push({ src: "cx_prot1", tgt: "cx_junction", type: "activate" });
          edgeList.push({ src: "cx_prot2", tgt: "cx_junction", type: "activate" });
          edgeList.push({ src: "cx_junction", tgt: "cx_complex", type: "activate" });
          if (complexPosForm) {
            nodeMap["cx_pos_reg"] = { id: "cx_pos_reg", type: "tf", label: "positive\nregulation (1)", x: 90, y: 200 };
            edgeList.push({ src: "cx_pos_reg", tgt: "cx_junction", type: "activate" });
          }
          if (complexNegForm) {
            nodeMap["cx_neg_reg"] = { id: "cx_neg_reg", type: "tf", label: "negative\nregulation (1)", x: 510, y: 200 };
            edgeList.push({ src: "cx_neg_reg", tgt: "cx_junction", type: "inhibit" });
          }
          if (baselineComplexAct) {
            nodeMap["cx_activity"] = { id: "cx_activity", type: "prot", label: "complex\nactivity", x: 300, y: 370 };
            edgeList.push({ src: "cx_complex", tgt: "cx_activity", type: "activate" });
            if (complexPosAct) {
              nodeMap["cx_pos_act_reg"] = { id: "cx_pos_act_reg", type: "tf", label: "positive\nregulation (2)", x: 100, y: 370 };
              edgeList.push({ src: "cx_pos_act_reg", tgt: "cx_activity", type: "activate" });
            }
            if (complexNegAct) {
              nodeMap["cx_neg_act_reg"] = { id: "cx_neg_act_reg", type: "tf", label: "negative\nregulation (2)", x: 500, y: 370 };
              edgeList.push({ src: "cx_neg_act_reg", tgt: "cx_activity", type: "inhibit" });
            }
          }
        } else if (baselineComplexAct) {
          nodeMap["cx_complex"] = { id: "cx_complex", type: "prot", label: "complex\namount", x: 300, y: 130 };
          nodeMap["cx_activity"] = { id: "cx_activity", type: "prot", label: "complex\nactivity", x: 300, y: 270 };
          edgeList.push({ src: "cx_complex", tgt: "cx_activity", type: "activate" });
          if (complexPosAct) {
            nodeMap["cx_pos_act_reg"] = { id: "cx_pos_act_reg", type: "tf", label: "positive\nregulation (2)", x: 100, y: 270 };
            edgeList.push({ src: "cx_pos_act_reg", tgt: "cx_activity", type: "activate" });
          }
          if (complexNegAct) {
            nodeMap["cx_neg_act_reg"] = { id: "cx_neg_act_reg", type: "tf", label: "negative\nregulation (2)", x: 500, y: 270 };
            edgeList.push({ src: "cx_neg_act_reg", tgt: "cx_activity", type: "inhibit" });
          }
        }
      }
    }

    // Indirect regulation motif
    if (mode === "other") {
      const indPosOn = checkedKeys.has("indirect-regulation:pos-reg");
      const indNegOn = checkedKeys.has("indirect-regulation:neg-reg");
      if (indPosOn || indNegOn) {
        Object.keys(nodeMap).forEach((k) => delete nodeMap[k]);
        edgeList.length = 0;
        nodeMap["ind_element"] = { id: "ind_element", type: "prot", label: "element", x: 300, y: 220 };
        if (indPosOn) {
          nodeMap["ind_pos"] = { id: "ind_pos", type: "dashed", label: "positive\nregulation", x: 140, y: 100 };
          edgeList.push({ src: "ind_pos", tgt: "ind_element", type: "activate", dashed: true });
        }
        if (indNegOn) {
          nodeMap["ind_neg"] = { id: "ind_neg", type: "dashed", label: "negative\nregulation", x: 460, y: 100 };
          edgeList.push({ src: "ind_neg", tgt: "ind_element", type: "inhibit", dashed: true });
        }
      }
    }

    // Chemical reaction motif
    if (mode === "other") {
      const chemPosOn = checkedKeys.has("chemical-reaction:pos-reg");
      const chemNegOn = checkedKeys.has("chemical-reaction:neg-reg");
      if (chemPosOn || chemNegOn) {
        Object.keys(nodeMap).forEach((k) => delete nodeMap[k]);
        edgeList.length = 0;
        nodeMap["chem_molecule"] = { id: "chem_molecule", type: "prot", label: "small\nmolecule", x: 300, y: 220 };
        if (chemPosOn) {
          nodeMap["chem_pos"] = { id: "chem_pos", type: "dashed", label: "positive\nregulation", x: 140, y: 100 };
          edgeList.push({ src: "chem_pos", tgt: "chem_molecule", type: "activate", dashed: true });
        }
        if (chemNegOn) {
          nodeMap["chem_neg"] = { id: "chem_neg", type: "dashed", label: "negative\nregulation", x: 460, y: 100 };
          edgeList.push({ src: "chem_neg", tgt: "chem_molecule", type: "inhibit", dashed: true });
        }
      }
    }

    // Biological process regulation motif
    if (mode === "other") {
      const bioPosOn = checkedKeys.has("biomarker-readout:pos-reg");
      const bioNegOn = checkedKeys.has("biomarker-readout:neg-reg");
      if (bioPosOn || bioNegOn) {
        Object.keys(nodeMap).forEach((k) => delete nodeMap[k]);
        edgeList.length = 0;
        nodeMap["bio_process"] = { id: "bio_process", type: "prot", label: "biological\nprocess", x: 300, y: 220, noteKey: "bio_process" };
        if (bioPosOn) {
          nodeMap["bio_pos"] = { id: "bio_pos", type: "dashed", label: "positive\nregulation", x: 140, y: 100 };
          edgeList.push({ src: "bio_pos", tgt: "bio_process", type: "activate", dashed: true });
        }
        if (bioNegOn) {
          nodeMap["bio_neg"] = { id: "bio_neg", type: "dashed", label: "negative\nregulation", x: 460, y: 100 };
          edgeList.push({ src: "bio_neg", tgt: "bio_process", type: "inhibit", dashed: true });
        }
      }
    }

    return { canvasNodes: Object.values(nodeMap), canvasEdges: edgeList };
  }, [checkedKeys, radioValues, mode]);

  return (
    <Root>
      <LeftPanel>
        <Title>Select motif type</Title>

        <ModeRow>
          <ModeBtn $active={mode === "biological"} onClick={() => switchMode("biological")}>
            Biological<br />motif
          </ModeBtn>
          <ModeBtn $active={mode === "other"} onClick={() => switchMode("other")}>
            Other<br />motif
          </ModeBtn>
          <ModeBtn $active={mode === "computational"} onClick={() => switchMode("computational")}>
            Computational<br />motifs
          </ModeBtn>
        </ModeRow>

        {mode === null && (
          <Placeholder>Select a motif type above to get started.</Placeholder>
        )}

        {mode === "biological" && (
          <Group>
            <GroupLabel>Gene–RNA–Protein (GRP) motifs</GroupLabel>
            {GRP_ITEMS.map((item) => (
              <div key={item.key} style={{ marginBottom: 6 }}>
                <SectionHeader>{item.label}</SectionHeader>
                <SubList style={{ marginBottom: 0 }}>
                  {item.sub.map((s) => (
                    <SubRowItem key={s.key} s={s} sectionKey={item.key}
                      checked={checkedKeys.has(item.key + ":" + s.key)} onToggle={toggleCheck} />
                  ))}
                </SubList>
              </div>
            ))}
          </Group>
        )}

        {mode === "other" && (
          <Group>
            <GroupLabel>Other motifs</GroupLabel>
            {OTHER_ITEMS.map((item) => (
              <CollapseSection key={item.key} item={item} checkedKeys={checkedKeys}
                onToggle={toggleCheck} radioValues={radioValues} onRadio={setRadio}
                openKeys={openKeys} onToggleOpen={toggleOpen} />
            ))}
          </Group>
        )}

        {mode === "computational" && (
          <Group>
            <GroupLabel>Computational motifs</GroupLabel>
            {COMPUTATIONAL_ITEMS.map((item) => (
              <CollapseSection key={item.key} item={item} checkedKeys={checkedKeys}
                onToggle={toggleCheck} openKeys={openKeys} onToggleOpen={toggleOpen} />
            ))}
          </Group>
        )}
      </LeftPanel>

      <RightPanel>
        {mode === "computational" ? (
          <>
            {/* ── Compare bar ───────────────────────────────── */}
            <CompareBar>
              <AddBtn onClick={addConfiguration} disabled={isDuplicate}>
                + Add to compare
              </AddBtn>
              {configurations.length > 0 && (
                <>
                  <ClearBtn onClick={clearConfigurations}>Clear all</ClearBtn>
                  <CompareCount>
                    {configurations.length} saved
                  </CompareCount>
                </>
              )}
            </CompareBar>

            <ComputationalMotifViewer
              checkedKeys={checkedKeys}
              configurations={configurations}
              onRemove={removeConfiguration}
              justAdded={justAdded}
              newestId={newestId}
            />
          </>
        ) : (
          <>
            <CanvasWrap>
              {mode !== "biological" &&
              checkedKeys.size === 0 &&
              !Object.values(radioValues).some((v) => v) ? (
                <EmptyState>
                  <EmptyIcon>
                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                      <circle cx="18" cy="18" r="17" stroke="#99b3d4" strokeWidth="1.5" strokeDasharray="4 3" />
                      <circle cx="18" cy="18" r="5" stroke="#99b3d4" strokeWidth="1.5" />
                    </svg>
                  </EmptyIcon>
                  <EmptyText>Choose a motif from the left to preview it here</EmptyText>
                </EmptyState>
              ) : (
                <MotifCanvas nodes={canvasNodes} edges={canvasEdges}
                  isBiological={mode === "biological"} activeNode={activeNode}
                  onNodeClick={setActiveNode} />
              )}
            </CanvasWrap>
            {mode === "biological" && (
              <ClickHint>Click any node to see its notes </ClickHint>
            )}
          </>
        )}

        <Legend>
          {Object.entries(NODE_COLOR).map(([type, color]) => (
            <LegendItem key={type}>
              <LegendDot $color={color} />
              {NODE_LABEL[type]}
            </LegendItem>
          ))}
          <LegendItem>
            <svg width="28" height="10">
              <defs>
                <marker id="leg-act" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <polygon points="0,0 6,3 0,6" fill="#66B2E3" />
                </marker>
              </defs>
              <line x1="0" y1="5" x2="22" y2="5" stroke="#66B2E3" strokeWidth="1.6" markerEnd="url(#leg-act)" />
            </svg>
            activation
          </LegendItem>
          <LegendItem>
            <svg width="28" height="10">
              <line x1="0" y1="5" x2="22" y2="5" stroke="#FF5B45" strokeWidth="1.6" strokeDasharray="4,2" />
              <line x1="22" y1="2" x2="22" y2="8" stroke="#FF5B45" strokeWidth="2" />
            </svg>
            inhibition
          </LegendItem>
        </Legend>
      </RightPanel>
    </Root>
  );
};

export default MotifSelector;